import React, {useEffect, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Keyboard, Navigation, Pagination} from 'swiper/modules';

import {SalonService} from '../config/salon';

type Props = {
  services: SalonService[];
  galleryServices?: SalonService[];
  className?: string;
};

export const ServiceGrid: React.FC<Props> = ({
  services,
  galleryServices,
  className = '',
}) => {
  const galleryItems = galleryServices ?? services;
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    if (!galleryOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setGalleryOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [galleryOpen]);

  const openGallery = (service: SalonService) => {
    const nextIndex = galleryItems.findIndex((item) => item.id === service.id);
    setGalleryIndex(nextIndex >= 0 ? nextIndex : 0);
    setGalleryOpen(true);
  };

  const renderGallery = (): JSX.Element | null => {
    if (!galleryOpen) return null;

    return (
      <div
        className='service-gallery'
        role='dialog'
        aria-modal='true'
      >
        <button
          className='service-gallery__close'
          onClick={() => setGalleryOpen(false)}
          type='button'
          aria-label='Hizmet galerisini kapat'
        >
          ×
        </button>
        <Swiper
          key={galleryIndex}
          className='service-gallery__slider'
          initialSlide={galleryIndex}
          keyboard={{
            enabled: true,
          }}
          loop={galleryItems.length > 1}
          modules={[Keyboard, Navigation, Pagination]}
          navigation={galleryItems.length > 1}
          pagination={{
            clickable: true,
          }}
        >
          {galleryItems.map((service) => {
            return (
              <SwiperSlide key={service.id}>
                <div className='service-gallery__slide'>
                  <img
                    className='service-gallery__image'
                    src={service.image}
                    alt={service.imageAlt}
                  />
                  <div className='service-gallery__caption'>
                    <span>{service.accent}</span>
                    <h3>{service.title}</h3>
                    <p>{service.summary}</p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  };

  return (
    <>
      <div className={`service-grid ${className}`.trim()}>
        {services.map((service) => {
          return (
            <button
              className='service-card'
              key={service.id}
              onClick={() => openGallery(service)}
              type='button'
            >
              <span className='service-card__media'>
                <img
                  className='service-card__image'
                  src={service.image}
                  alt={service.imageAlt}
                  loading='lazy'
                />
              </span>
              <span className='service-card__body'>
                <span className='service-card__accent'>{service.accent}</span>
                <span className='service-card__title'>{service.title}</span>
                <span className='service-card__summary'>{service.summary}</span>
              </span>
            </button>
          );
        })}
      </div>
      {renderGallery()}
    </>
  );
};
